/* eslint-disable react/require-default-props */
import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useIntl } from "react-intl";
import * as pdfjsLib from "pdfjs-dist";
import { FlexColumn, Typography } from "design-system";

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL || ""}/js/pdf.worker.min.mjs`;

const PDF_RENDER_SCALE = 1.35;

async function loadPdfDocument(src) {
  const response = await fetch(src);

  if (!response.ok) {
    throw new Error(`Failed to fetch PDF (${response.status})`);
  }

  const data = new Uint8Array(await response.arrayBuffer());
  return pdfjsLib.getDocument({ data }).promise;
}

export default function PdfDocumentViewer({
  src,
  title,
  className = "",
  onReady,
}) {
  const intl = useIntl();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const onReadyRef = useRef(onReady);
  const readyNotifiedRef = useRef(false);

  onReadyRef.current = onReady;

  useEffect(() => {
    let cancelled = false;

    const loadPdf = async () => {
      setLoading(true);
      setError(null);
      setPages([]);
      readyNotifiedRef.current = false;

      try {
        const pdf = await loadPdfDocument(src);
        const renderedPages = [];

        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
          const page = await pdf.getPage(pageNumber);
          const viewport = page.getViewport({ scale: PDF_RENDER_SCALE });
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d");

          canvas.height = viewport.height;
          canvas.width = viewport.width;

          await page.render({
            canvasContext: context,
            viewport,
            canvas,
          }).promise;

          renderedPages.push({
            pageNumber,
            src: canvas.toDataURL(),
          });
        }

        if (!cancelled) {
          setPages(renderedPages);
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(loadError);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadPdf();

    return () => {
      cancelled = true;
    };
  }, [src]);

  useEffect(() => {
    if (loading || error || pages.length === 0 || readyNotifiedRef.current) {
      return;
    }

    readyNotifiedRef.current = true;
    onReadyRef.current?.();
  }, [error, loading, pages.length]);

  if (loading) {
    return (
      <FlexColumn
        align="center"
        justify="center"
        fullWidth
        className={className}
      >
        <Typography component="p" variant="bodyMedium" color="ui-subtle">
          {intl.formatMessage({ id: "ld-loading-document" })}
        </Typography>
      </FlexColumn>
    );
  }

  if (error) {
    return (
      <FlexColumn
        align="start"
        fullWidth
        gap="var(--ds-space-2)"
        className={className}
      >
        <Typography component="p" variant="bodyMedium" color="ui-subtle">
          {intl.formatMessage({ id: "ld-pdf-preview-unavailable" })}
        </Typography>
      </FlexColumn>
    );
  }

  return (
    <FlexColumn
      align="stretch"
      fullWidth
      gap="var(--ds-space-3)"
      className={className}
    >
      {pages.map((page) => (
        <img
          key={page.pageNumber}
          src={page.src}
          alt={intl.formatMessage(
            { id: "ld-pdf-page-alt" },
            { title, page: page.pageNumber },
          )}
          className="ld-document__pdf-page"
        />
      ))}
    </FlexColumn>
  );
}

PdfDocumentViewer.propTypes = {
  src: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  className: PropTypes.string,
  onReady: PropTypes.func,
};
